import React from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import { QuestionLayout } from "@src/packages/medical-history/components";
import { SquareOptionButton } from "@src/packages/medical-history/components/SquareOptionButton";
import { useTranslations } from "@src/packages/i18n";
import { useSleepActivity } from "../SleepActivityContext";
import { RangeSlider } from "../components/RangeSlider";
import { ThumbsUp, ThumbsDown } from "lucide-react-native";
import type { SleepActivityStackParamList } from "../SleepActivityNavigator";
import { useMedicalHistoryTheme } from "../../../theme/MedicalHistoryTheme";

const EXERCISE_MIN = 2;
const EXERCISE_MAX = 10;
const DEFAULT_MIN_EXERCISE = 4;
const DEFAULT_MAX_EXERCISE = 7;

export function ActivityScreen() {
  const t = useTranslations();
  const { state, updateState } = useSleepActivity();
  const navigation =
    useNavigation<NavigationProp<SleepActivityStackParamList>>();
  const theme = useMedicalHistoryTheme();

  const handleYes = () => {
    updateState({
      isExercising: true,
      minHoursOfExercise: state.minHoursOfExercise ?? DEFAULT_MIN_EXERCISE,
      maxHoursOfExercise: state.maxHoursOfExercise ?? DEFAULT_MAX_EXERCISE,
    });
  };

  const handleNo = () => {
    updateState({
      isExercising: false,
      minHoursOfExercise: null,
      maxHoursOfExercise: null,
    });
  };

  const handleSave = () => {
    // Persist exercise defaults if user selected Yes but didn't touch the slider
    if (
      state.isExercising === true &&
      (state.minHoursOfExercise === null || state.maxHoursOfExercise === null)
    ) {
      updateState({
        minHoursOfExercise: DEFAULT_MIN_EXERCISE,
        maxHoursOfExercise: DEFAULT_MAX_EXERCISE,
      });
    }
    navigation.navigate("SleepActivityComplete");
  };

  return (
    <QuestionLayout
      title={t("med_history.lifestyle.sleep_activity.activity_title")}
      subtitle={t("med_history.lifestyle.sleep_activity.activity_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={state.isExercising === null}
      saveLabel={t("med_history.lifestyle.next")}
    >
      <View style={styles.container}>
        {/* Yes / No buttons */}
        <View style={styles.optionsRow}>
          <SquareOptionButton
            icon={ThumbsUp}
            label={t("med_history.shared.yes")}
            isSelected={state.isExercising === true}
            onPress={handleYes}
          />
          <SquareOptionButton
            icon={ThumbsDown}
            label={t("med_history.shared.no")}
            isSelected={state.isExercising === false}
            onPress={handleNo}
          />
        </View>

        {/* Conditional exercise hours slider */}
        {state.isExercising === true && (
          <View style={styles.sliderSection}>
            <Text
              style={{
                marginBottom: 16,
                textAlign: "center",
                fontSize: 16,
                fontWeight: "500",
                color: theme.text,
              }}
            >
              {t("med_history.lifestyle.sleep_activity.activity_slider_label")}
            </Text>
            <RangeSlider
              min={EXERCISE_MIN}
              max={EXERCISE_MAX}
              lowValue={state.minHoursOfExercise ?? DEFAULT_MIN_EXERCISE}
              highValue={state.maxHoursOfExercise ?? DEFAULT_MAX_EXERCISE}
              onValuesChange={(low, high) =>
                updateState({
                  minHoursOfExercise: low,
                  maxHoursOfExercise: high,
                })
              }
              lowLabel="<2"
              highLabel="10+"
              formatValue={(hours) =>
                t("med_history.lifestyle.sleep_activity.hours_count", {
                  params: { count: String(hours) },
                })
              }
            />
          </View>
        )}
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    paddingHorizontal: 8,
    marginTop: 24,
    marginBottom: 40,
  },
  optionsRow: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 24,
  },
  sliderSection: {
    marginTop: 16,
  },
});
